/**
 * Set Type Badge with Dropdown
 * Displays set type (normal, warmup, etc.) with dropdown selection
 */

import { useEffect, useRef, useState } from 'react'
import { cn } from '../../lib/utils'
import type { SetType } from '../../stores/splitConfigStore'

interface SetTypeBadgeProps {
  type: SetType
  setNumber: number
  normalSetNumber: number // Number among normal sets only
  onChange: (type: SetType) => void
  className?: string
}

// Set type colors matching the app's design system
const SET_TYPE_COLORS: Record<SetType, { bg: string; text: string; border: string }> = {
  normal: {
    bg: 'bg-primary/10',
    text: 'text-primary',
    border: 'border-primary/20'
  },
  warmup: {
    bg: 'bg-orange-500/10',
    text: 'text-orange-600 dark:text-orange-500',
    border: 'border-orange-500/20'
  },
  failure: {
    bg: 'bg-red-500/10',
    text: 'text-red-600 dark:text-red-500',
    border: 'border-red-500/20'
  },
  dropset: {
    bg: 'bg-purple-500/10',
    text: 'text-purple-600 dark:text-purple-500',
    border: 'border-purple-500/20'
  }
}

const SET_TYPE_LABELS: Record<SetType, string> = {
  normal: 'Normal',
  warmup: 'Warmup',
  failure: 'Failure',
  dropset: 'Dropset'
}

const SET_TYPE_DISPLAY: Record<SetType, string> = {
  normal: '', // Will use normalSetNumber
  warmup: 'W',
  failure: 'F',
  dropset: 'D'
}

export const SetTypeBadge = ({ type, setNumber: _setNumber, normalSetNumber, onChange, className }: SetTypeBadgeProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const colors = SET_TYPE_COLORS[type]
  
  // Close dropdown when clicking outside or pressing ESC
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleKeyDown)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
        document.removeEventListener('keydown', handleKeyDown)
      }
    }
  }, [isOpen])
  
  const handleTypeChange = (newType: SetType) => {
    onChange(newType)
    setIsOpen(false)
  }
  
  return (
    <div ref={dropdownRef} className="relative">
      {/* Trigger Badge */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'inline-flex items-center justify-center min-w-[1.75rem] h-6 px-1.5 rounded text-xs font-medium border transition-all cursor-pointer',
          colors.bg,
          colors.text,
          colors.border,
          'hover:opacity-80',
          className
        )}
      >
        {type === 'normal' ? normalSetNumber : SET_TYPE_DISPLAY[type]}
      </button>
      
      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className={cn(
            'absolute top-full left-0 mt-1 min-w-[140px] bg-popover border border-border rounded-md shadow-md z-50',
            'animate-in fade-in-0 zoom-in-95 slide-in-from-top-2 duration-200'
          )}
        >
          {(['normal', 'warmup', 'failure', 'dropset'] as SetType[]).map((setType) => {
            const typeColors = SET_TYPE_COLORS[setType]
            return (
              <button
                key={setType}
                type="button"
                onClick={() => handleTypeChange(setType)}
                className={cn(
                  'flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors cursor-pointer',
                  type === setType && 'bg-accent/50'
                )}
              >
                <span
                  className={cn(
                    'inline-flex items-center justify-center w-5 h-5 rounded text-xs font-medium border',
                    typeColors.bg,
                    typeColors.text,
                    typeColors.border
                  )}
                >
                  {setType === 'normal' ? _setNumber : SET_TYPE_DISPLAY[setType]}
                </span>
                <span>{SET_TYPE_LABELS[setType]}</span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}